/**
 * Tour Spotlight Component
 *
 * Creates a full-screen overlay with a cutout effect
 * that highlights the current tour target element.
 */

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTour } from './TourProvider';

interface SpotlightRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function TourSpotlight() {
  const { isActive, currentStep, getTargetRect, skipTour } = useTour();
  const [targetRect, setTargetRect] = useState<SpotlightRect | null>(null);

  // Update target rect when step changes or on resize
  const updateRect = useCallback(() => {
    if (!currentStep) {
      setTargetRect(null);
      return;
    }

    // Center placement doesn't need a target element
    if (currentStep.placement === 'center') {
      setTargetRect(null);
      return;
    }

    const rect = getTargetRect(currentStep.targetId);
    if (rect) {
      const padding = currentStep.spotlightPadding ?? 4;
      setTargetRect({
        x: rect.left - padding,
        y: rect.top - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
      });
    } else {
      setTargetRect(null);
    }
  }, [currentStep, getTargetRect]);

  useEffect(() => {
    updateRect();

    // Update on resize and scroll
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);

    // Also update periodically to catch dynamic content
    const interval = setInterval(updateRect, 100);

    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
      clearInterval(interval);
    };
  }, [updateRect]);

  // Handle click outside spotlight (on overlay)
  const handleOverlayClick = (e: React.MouseEvent) => {
    // Only handle clicks directly on the overlay, not on children
    if (e.target === e.currentTarget) {
      // Optional: could show "Skip tour?" confirmation
      // For now, just skip
      skipTour();
    }
  };

  if (!isActive) return null;

  const content = (
    <AnimatePresence>
      {isActive && (
        <motion.div
          className="tour-spotlight-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleOverlayClick}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 250,
            pointerEvents: 'auto',
          }}
        >
          {/* SVG mask for spotlight cutout */}
          <svg
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}
          >
            <defs>
              <mask id="spotlight-mask">
                {/* White = visible, black = transparent */}
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                {targetRect && (
                  <motion.rect
                    initial={{
                      opacity: 0,
                      x: targetRect.x,
                      y: targetRect.y,
                      width: targetRect.width,
                      height: targetRect.height,
                    }}
                    animate={{
                      x: targetRect.x,
                      y: targetRect.y,
                      width: targetRect.width,
                      height: targetRect.height,
                      opacity: 1,
                    }}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    rx="8"
                    ry="8"
                    fill="black"
                  />
                )}
              </mask>
            </defs>
            {/* Dark overlay with cutout */}
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill="rgba(0, 0, 0, 0.85)"
              mask="url(#spotlight-mask)"
              style={{ pointerEvents: 'auto' }}
            />
          </svg>

          {/* Glow effect around target */}
          {targetRect && (
            <motion.div
              className="tour-spotlight-glow"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{
                opacity: 1,
                scale: 1,
                x: targetRect.x,
                y: targetRect.y,
              }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: targetRect.width,
                height: targetRect.height,
                borderRadius: '8px',
                boxShadow: `
                  0 0 0 3px var(--accent-growth),
                  0 0 20px rgba(127, 255, 0, 0.4),
                  0 0 40px rgba(127, 255, 0, 0.2)
                `,
                pointerEvents: 'none',
              }}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Portal to body for proper z-index stacking
  return createPortal(content, document.body);
}
